import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Printer, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, Dialog, Field, Input, Select, Spinner, Textarea, buttonStyles } from '@zt/ui';
import { api, ApiError, IdempotencyKey } from '../lib/api';
import { useStoreChoice } from '../lib/store-choice';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { bpsLabel, minorToInput, toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  BulkOrderStatusResult,
  CustomerPage,
  OrderDetail,
  OrderPrint,
  ProductPage,
  ShippingMethodRow,
} from '../lib/types';
import { ProductVariantPicker } from './stock';
import { TrPlaceFields } from '../lib/tr-place';
import { CreditOverrideField, creditOverrideBody } from '../components/CreditOverride';
import { LocationFill } from '../components/CompanyLocations';

/**
 * Sipariş araçları (F5B-12, ADR-068): panelden sipariş/taslak formu (kayıtlı müşteri veya misafir, kargo,
 * indirim), taslak ve sipariş düzenleme pencereleri, sipariş listesinde toplu işlem çubuğu ve yazdırma sayfası.
 */

type T = (k: string, p?: Record<string, string | number>) => string;

/** Çeviri yoksa (anahtarın kendisi dönerse) yedek metin. */
function tOr(t: T, key: string, fallback: string, params?: Record<string, string | number>) {
  const v = t(key, params);
  return v === key ? fallback : v;
}

// ---------------------------------------------------------------------------
// Ortak parçalar: ürün satırları ve adres alanları
// ---------------------------------------------------------------------------

export interface FormLine {
  variantId: string;
  label: string;
  quantity: string;
  unitPrice: string;
}

export function LinesEditor({
  lines,
  onChange,
  canPrice,
  currency,
}: {
  lines: FormLine[];
  onChange: (lines: FormLine[]) => void;
  canPrice: boolean;
  currency: string;
}) {
  const t = useT();
  const [productQ, setProductQ] = useState('');
  const products = useQuery({
    queryKey: ['products', 'pick', productQ],
    queryFn: () =>
      api.get<ProductPage>(`/v1/admin/products?q=${encodeURIComponent(productQ)}&status=active&limit=8`),
    enabled: productQ.trim().length >= 2,
  });
  return (
    <div className="space-y-2">
      <Field label={t('admin.orders.addProduct')}>
        {(p) => <Input {...p} type="search" value={productQ} onChange={(e) => setProductQ(e.target.value)} />}
      </Field>
      {products.data && productQ.trim().length >= 2 && (
        <ProductVariantPicker
          productIds={products.data.items.map((i) => ({ id: i.id, title: i.title }))}
          onPick={(variantId, label) => {
            onChange(
              lines.some((l) => l.variantId === variantId)
                ? lines
                : [...lines, { variantId, label, quantity: '1', unitPrice: '' }],
            );
            setProductQ('');
          }}
        />
      )}
      {lines.length > 0 && (
        <ul className="space-y-2">
          {lines.map((l, i) => (
            <li
              key={l.variantId}
              className="grid grid-cols-[1fr_5rem_auto] items-center gap-2 sm:grid-cols-[1fr_5rem_8rem_auto]"
            >
              <span className="text-sm">{l.label}</span>
              <Input
                aria-label={t('admin.orders.qty')}
                className="h-9 tabular"
                inputMode="numeric"
                value={l.quantity}
                onChange={(e) =>
                  onChange(lines.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))
                }
              />
              {canPrice && (
                <Input
                  aria-label={t('admin.orders.manualPrice')}
                  placeholder={t('admin.orders.manualPricePlaceholder')}
                  className="h-9 tabular"
                  inputMode="decimal"
                  value={l.unitPrice}
                  onChange={(e) =>
                    onChange(lines.map((x, j) => (j === i ? { ...x, unitPrice: e.target.value } : x)))
                  }
                />
              )}
              <Button
                size="icon"
                variant="ghost"
                aria-label={t('common.remove')}
                onClick={() => onChange(lines.filter((_, j) => j !== i))}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </Button>
            </li>
          ))}
        </ul>
      )}
      {canPrice && lines.length > 0 && (
        <p className="text-sm text-ink-3">{t('admin.orderTools.manualPriceHint', { currency })}</p>
      )}
    </div>
  );
}

export function linesPayload(lines: FormLine[], canPrice: boolean, currency: string) {
  return lines.map((l) => ({
    variantId: l.variantId,
    quantity: Number(l.quantity) || 1,
    ...(canPrice && l.unitPrice.trim()
      ? { unitPriceMinor: toMinor(l.unitPrice, currency) ?? undefined }
      : {}),
  }));
}

function linesFromOrder(o: OrderDetail): FormLine[] {
  return o.lines
    .filter((l) => l.variantId)
    .map((l) => ({
      variantId: l.variantId!,
      label: `${l.title} (${l.sku})`,
      quantity: String(l.quantity),
      unitPrice: l.priceListId === null ? minorToInput(l.unitPriceMinor) : '',
    }));
}

interface Addr {
  fullName: string;
  phone: string;
  line1: string;
  district: string;
  city: string;
}
const EMPTY_ADDR: Addr = { fullName: '', phone: '', line1: '', district: '', city: '' };

function addrOf(a: Record<string, unknown> | null | undefined): Addr {
  if (!a || a.masked) return EMPTY_ADDR;
  const s = (k: string) => (typeof a[k] === 'string' ? (a[k] as string) : '');
  return {
    fullName: s('fullName'),
    phone: s('phone'),
    line1: s('line1'),
    district: s('district'),
    city: s('city'),
  };
}

const addrComplete = (a: Addr) => !!(a.fullName.trim() && a.phone.trim() && a.line1.trim() && a.city.trim());

function addrPayload(a: Addr, base?: Record<string, unknown> | null) {
  const extra =
    base && !base.masked
      ? {
          line2: (base.line2 as string | null | undefined) ?? null,
          postalCode: (base.postalCode as string | null | undefined) ?? null,
          companyName: (base.companyName as string | null | undefined) ?? null,
          taxOffice: (base.taxOffice as string | null | undefined) ?? null,
          taxNumber: (base.taxNumber as string | null | undefined) ?? null,
        }
      : {};
  return { ...extra, ...a, district: a.district || null, countryCode: 'TR' };
}

function AddressFields({
  value,
  onChange,
  err,
  prefix,
  required,
}: {
  value: Addr;
  onChange: (a: Addr) => void;
  err: unknown;
  prefix: 'shippingAddress' | 'billingAddress';
  required: boolean;
}) {
  const t = useT();
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field
        label={t('admin.orders.fullName')}
        required={required}
        error={fieldError(err, `${prefix}.fullName`)}
      >
        {(p) => (
          <Input
            {...p}
            value={value.fullName}
            onChange={(e) => onChange({ ...value, fullName: e.target.value })}
          />
        )}
      </Field>
      <Field
        label={t('admin.customers.phone')}
        required={required}
        error={fieldError(err, `${prefix}.phone`)}
      >
        {(p) => (
          <Input
            {...p}
            type="tel"
            value={value.phone}
            onChange={(e) => onChange({ ...value, phone: e.target.value })}
          />
        )}
      </Field>
      <Field
        label={t('admin.orders.addressLine')}
        required={required}
        className="sm:col-span-2"
        error={fieldError(err, `${prefix}.line1`)}
      >
        {(p) => (
          <Input {...p} value={value.line1} onChange={(e) => onChange({ ...value, line1: e.target.value })} />
        )}
      </Field>
      {/* İl ve ilçe listeden (F5B-17). */}
      <TrPlaceFields
        city={value.city}
        district={value.district}
        onChange={(v) => onChange({ ...value, ...v })}
        cityLabel={t('admin.dealers.city')}
        districtLabel={t('admin.dealers.district')}
        required={required}
        cityError={fieldError(err, `${prefix}.city`)}
        districtError={fieldError(err, `${prefix}.district`)}
      />
    </div>
  );
}

function ErrorList({ err }: { err: ApiError | null }) {
  if (!err) return null;
  return (
    <p role="alert" className="text-sm text-rose">
      {err.errors.length ? err.errors.map((e) => e.message).join(' · ') : err.title}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Panelden sipariş / taslak formu
// ---------------------------------------------------------------------------

type PaymentMethod = 'manual' | 'bank_transfer' | 'open_account' | 'cash_on_delivery';
type DiscountKind = 'none' | 'code' | 'manual';

/** `order` verilirse taslağı düzenler (PATCH); yoksa yeni sipariş/taslak oluşturur. */
export function OrderFormDialog({ order, onClose }: { order?: OrderDetail; onClose: () => void }) {
  const t = useT();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const pii = can('customers.pii.read');
  const canPrice = can('pricing.price.write');
  // Yeni sipariş üst çubuktaki çalışılan mağazaya açılır (F5B-21); düzenlemede siparişin mağazası.
  const choice = useStoreChoice();
  const storeId = order?.storeId ?? (choice.storeId || undefined);
  // Yeni siparişin para birimi mağazanın varsayılan pazarından (F7-11).
  const store = choice.stores.find((s) => s.id === storeId);
  const currency =
    order?.currency ?? store?.markets.find((m) => m.id === store.defaultMarketId)?.currency ?? 'TRY';
  const methods = useQuery({
    queryKey: ['shipping-methods', storeId],
    queryFn: () => api.get<ShippingMethodRow[]>(`/v1/admin/shipping-methods?storeId=${storeId}`),
    enabled: !!storeId,
  });
  const [idem] = useState(() => new IdempotencyKey());
  const [who, setWho] = useState<'customer' | 'guest'>(order && !order.customerId ? 'guest' : 'customer');
  const [customerQ, setCustomerQ] = useState('');
  const [customer, setCustomer] = useState<{ id: string; name: string; companyId: string | null } | null>(
    order?.customerId
      ? { id: order.customerId, name: order.customerName ?? '', companyId: order.companyId ?? null }
      : null,
  );
  // Kişisel veri yetkisi yoksa e-posta, telefon ve adres maskeli gelir: alanlar boş başlar, yalnızca
  // doldurulursa gönderilir (maskeli değer siparişe yazılmasın).
  const [email, setEmail] = useState(order && !order.customerId && pii ? (order.email ?? '') : '');
  const [phone, setPhone] = useState(order && !order.customerId && pii ? (order.phone ?? '') : '');
  const [lines, setLines] = useState<FormLine[]>(order ? linesFromOrder(order) : []);
  const [shippingMethodId, setShippingMethodId] = useState(order?.shippingMethodId ?? '');
  const [shippingFee, setShippingFee] = useState(
    order?.shippingFeeManual ? minorToInput(order.shippingMinor) : '',
  );
  const [discountKind, setDiscountKind] = useState<DiscountKind>(
    order?.discountCode ? 'code' : order?.manualDiscountMinor ? 'manual' : 'none',
  );
  const [discountCode, setDiscountCode] = useState(order?.discountCode ?? '');
  const [manualDiscount, setManualDiscount] = useState(minorToInput(order?.manualDiscountMinor ?? null));
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    (order?.paymentMethod as PaymentMethod | null) ?? 'manual',
  );
  const [status, setStatus] = useState<'awaiting_payment' | 'draft'>('awaiting_payment');
  const [note, setNote] = useState(order?.note ?? '');
  // Bayinin satın alma emri numarası (F7-07); kayıtlı siparişte sipariş ayrıntısından düzenlenir.
  const [poNumber, setPoNumber] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const initialAddr = addrOf(order?.shippingAddress);
  const [addr, setAddr] = useState<Addr>(initialAddr);
  const addressMasked = !!order?.shippingAddress?.masked;
  const customers = useQuery({
    queryKey: ['customers', 'pick', customerQ],
    queryFn: () => api.get<CustomerPage>(`/v1/admin/customers?q=${encodeURIComponent(customerQ)}&limit=8`),
    enabled: who === 'customer' && customerQ.trim().length >= 2 && !customer,
  });
  const pricing = () => ({
    customerId: who === 'customer' ? (customer?.id ?? null) : null,
    ...(who === 'guest' && (email.trim() || !order) ? { email: email.trim() || null } : {}),
    ...(who === 'guest' && (phone.trim() || !order) ? { phone: phone.trim() || null } : {}),
    lines: linesPayload(lines, canPrice, currency),
    discountCode: discountKind === 'code' ? discountCode.trim() || null : null,
    manualDiscountMinor:
      discountKind === 'manual' && canPrice && manualDiscount.trim()
        ? (toMinor(manualDiscount, currency) ?? -1)
        : null,
    shippingMethodId: shippingMethodId || null,
    shippingMinor: canPrice && shippingFee.trim() ? (toMinor(shippingFee, currency) ?? -1) : null,
    paymentMethod,
    // Kredi limiti aşımı gerekçesi (F7-02): yalnızca limit hatasından sonra yazılır.
    ...creditOverrideBody(creditReason),
  });
  const save = useApiMutation(
    () => {
      if (order) {
        const addrChanged = addrComplete(addr) && JSON.stringify(addr) !== JSON.stringify(initialAddr);
        return api.patch<{ id: string }>(
          `/v1/admin/orders/${order.id}`,
          {
            ...pricing(),
            ...(addrChanged ? { shippingAddress: addrPayload(addr, order.shippingAddress) } : {}),
            note: note.trim() || null,
          },
          { ifMatch: order.version },
        );
      }
      return api.post<{ id: string }>(
        '/v1/admin/orders',
        {
          storeId,
          ...pricing(),
          shippingAddress: addrPayload(addr),
          note: note.trim() || undefined,
          ...(who === 'customer' && poNumber.trim() ? { poNumber: poNumber.trim() } : {}),
          status,
        },
        { idempotencyKey: idem },
      );
    },
    {
      invalidate: order ? [['order', order.id], ['orders']] : [['orders']],
      success: order ? t('common.saved') : t('admin.orders.created'),
      silentFieldErrors: true,
      onSuccess: (r) => {
        onClose();
        if (!order) void navigate({ to: '/siparisler/$id', params: { id: r.id } });
      },
    },
  );
  const err = save.error;
  const ready =
    lines.length > 0 &&
    (who === 'customer' ? !!customer : !!email.trim() || !!order) &&
    (order ? true : addrComplete(addr));
  const activeMethods = methods.data?.filter((m) => m.status === 'active' && m.currency === currency) ?? [];
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      variant="sheet"
      title={
        order
          ? t('admin.orderTools.editDraftTitle', { number: order.number })
          : t('admin.orders.createManual')
      }
      description={t('admin.orders.manualLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!ready}
            onClick={() => save.mutate(undefined)}
          >
            {order ? t('admin.orderTools.saveDraft') : t('admin.orders.createButton')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <ErrorList err={err} />
        <CreditOverrideField error={err} value={creditReason} onChange={setCreditReason} />
        <fieldset className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <legend className="mb-1.5 flex items-center gap-1 text-sm font-medium">
            {t('admin.orderTools.customerKind')} <HelpTip k="orders.guest" />
          </legend>
          {(['customer', 'guest'] as const).map((k) => (
            <label key={k} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="order-customer-kind"
                className="accent-[var(--zt-pine)]"
                checked={who === k}
                onChange={() => setWho(k)}
              />
              {t(`admin.orderTools.customerKinds.${k}`)}
            </label>
          ))}
        </fieldset>
        {who === 'customer' ? (
          customer ? (
            <div className="flex items-center justify-between rounded-sm border border-line px-3 py-2">
              <span className="font-medium">{customer.name}</span>
              <Button size="sm" variant="ghost" onClick={() => setCustomer(null)}>
                {t('admin.orders.changeCustomer')}
              </Button>
            </div>
          ) : (
            <>
              <Field label={t('admin.orders.customer')} description={t('admin.orders.customerHint')}>
                {(p) => (
                  <Input
                    {...p}
                    type="search"
                    value={customerQ}
                    onChange={(e) => setCustomerQ(e.target.value)}
                  />
                )}
              </Field>
              {customers.data && (
                <ul className="divide-y divide-line rounded-md border border-line text-sm">
                  {customers.data.items.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        className="w-full px-3 py-2 text-start hover:bg-surface-2"
                        onClick={() =>
                          setCustomer({
                            id: c.id,
                            name: c.name,
                            companyId: c.companies.find((x) => x.status === 'approved')?.companyId ?? null,
                          })
                        }
                      >
                        {c.name} <span className="text-ink-3">{c.email}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label={t('admin.orderTools.guestEmail')}
              required={!order}
              description={
                order && !pii ? t('admin.orderTools.maskedKeep') : t('admin.orderTools.guestEmailHint')
              }
              error={fieldError(err, 'email')}
            >
              {(p) => <Input {...p} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />}
            </Field>
            <Field
              label={t('admin.orderTools.guestPhone')}
              optionalLabel={t('common.optional')}
              error={fieldError(err, 'phone')}
            >
              {(p) => <Input {...p} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />}
            </Field>
          </div>
        )}
        <LinesEditor lines={lines} onChange={setLines} canPrice={canPrice} currency={currency} />
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label={t('admin.orders.shippingMethod')}
            help={<HelpTip k="orders.shipping_fee" />}
            error={fieldError(err, 'shippingMethodId')}
          >
            {(p) => (
              <Select {...p} value={shippingMethodId} onChange={(e) => setShippingMethodId(e.target.value)}>
                <option value="">{t('admin.orderTools.noShippingMethod')}</option>
                {activeMethods.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                    {m.audience !== 'all' ? ` (${t(`admin.orderTools.audience.${m.audience}`)})` : ''}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {canPrice && (
            <Field
              label={t('admin.orderTools.shippingFee', { currency })}
              optionalLabel={t('common.optional')}
              description={t('admin.orderTools.shippingFeeHint')}
              error={fieldError(err, 'shippingMinor')}
            >
              {(p) => (
                <Input
                  {...p}
                  inputMode="decimal"
                  className="tabular"
                  value={shippingFee}
                  onChange={(e) => setShippingFee(e.target.value)}
                />
              )}
            </Field>
          )}
          <Field label={t('admin.orderTools.discountKind')} help={<HelpTip k="orders.manual_discount" />}>
            {(p) => (
              <Select
                {...p}
                value={discountKind}
                onChange={(e) => setDiscountKind(e.target.value as DiscountKind)}
              >
                <option value="none">{t('admin.orderTools.discountKinds.none')}</option>
                <option value="code">{t('admin.orderTools.discountKinds.code')}</option>
                {canPrice && <option value="manual">{t('admin.orderTools.discountKinds.manual')}</option>}
              </Select>
            )}
          </Field>
          {discountKind === 'code' && (
            <Field label={t('admin.orders.discountCode')} required error={fieldError(err, 'discountCode')}>
              {(p) => (
                <Input
                  {...p}
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                />
              )}
            </Field>
          )}
          {discountKind === 'manual' && canPrice && (
            <Field
              label={t('admin.orderTools.manualDiscount', { currency })}
              required
              description={t('admin.orderTools.manualDiscountHint')}
              error={fieldError(err, 'manualDiscountMinor')}
            >
              {(p) => (
                <Input
                  {...p}
                  inputMode="decimal"
                  className="tabular"
                  value={manualDiscount}
                  onChange={(e) => setManualDiscount(e.target.value)}
                />
              )}
            </Field>
          )}
          <Field label={t('admin.orders.paymentMethod')} help={<HelpTip k="orders.manual_payment_method" />}>
            {(p) => (
              <Select
                {...p}
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
              >
                {(['manual', 'bank_transfer', 'open_account', 'cash_on_delivery'] as const).map((m) => (
                  <option key={m} value={m}>
                    {t(`admin.orders.paymentMethods.${m}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {!order && (
            <Field label={t('admin.orders.initialStatus')}>
              {(p) => (
                <Select {...p} value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
                  <option value="awaiting_payment">{t('admin.orders.initialStatuses.confirmed')}</option>
                  <option value="draft">{t('admin.orders.initialStatuses.draft')}</option>
                </Select>
              )}
            </Field>
          )}
        </div>
        <div>
          <p className="mb-2 text-sm font-medium">{t('admin.orders.shippingAddress')}</p>
          {addressMasked && <p className="mb-2 text-sm text-ink-3">{t('admin.orderTools.addressMasked')}</p>}
          {/* Bayi siparişinde firma lokasyonundan doldur (F7-04). */}
          {who === 'customer' && customer?.companyId && can('b2b.dealer.read') && (
            <div className="mb-3">
              <LocationFill
                companyId={customer.companyId}
                onPick={(l) =>
                  setAddr({
                    fullName: l.fullName,
                    phone: pii ? (l.phone ?? '') : addr.phone,
                    line1: l.line1,
                    district: l.district ?? '',
                    city: l.city,
                  })
                }
              />
            </div>
          )}
          <AddressFields
            value={addr}
            onChange={setAddr}
            err={err}
            prefix="shippingAddress"
            required={!order}
          />
        </div>
        <Field label={t('admin.orders.customerNote')} optionalLabel={t('common.optional')}>
          {(p) => (
            <Textarea {...p} className="min-h-16" value={note} onChange={(e) => setNote(e.target.value)} />
          )}
        </Field>
        {!order && who === 'customer' && (
          <Field
            label={t('admin.orders.po.number')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="orders.po_number" />}
            error={fieldError(save.error, 'poNumber')}
          >
            {(p) => (
              <Input
                {...p}
                className="w-56"
                maxLength={50}
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
              />
            )}
          </Field>
        )}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Sipariş düzenleme: ürünler (ödeme öncesi) ve adres/not (gönderiye kadar)
// ---------------------------------------------------------------------------

export function OrderLinesDialog({ order, onClose }: { order: OrderDetail; onClose: () => void }) {
  const t = useT();
  const { can } = usePermissions();
  const canPrice = can('pricing.price.write');
  const [lines, setLines] = useState<FormLine[]>(() =>
    order.lines
      .filter((l) => l.variantId)
      .map((l) => ({
        variantId: l.variantId!,
        label: `${l.title} (${l.sku})`,
        quantity: String(l.quantity),
        unitPrice: '',
      })),
  );
  const save = useApiMutation(
    () =>
      api.patch(
        `/v1/admin/orders/${order.id}`,
        { lines: linesPayload(lines, canPrice, order.currency) },
        { ifMatch: order.version },
      ),
    {
      invalidate: [['order', order.id], ['orders']],
      success: t('common.saved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      variant="sheet"
      title={t('admin.orderTools.editLinesTitle')}
      description={t('admin.orderTools.editLinesLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!lines.length}
            onClick={() => save.mutate(undefined)}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ErrorList err={save.error} />
        <LinesEditor lines={lines} onChange={setLines} canPrice={canPrice} currency={order.currency} />
      </div>
    </Dialog>
  );
}

export function OrderAddressDialog({ order, onClose }: { order: OrderDetail; onClose: () => void }) {
  const t = useT();
  const initialShip = addrOf(order.shippingAddress);
  const initialBill = addrOf(order.billingAddress);
  const masked = !!order.shippingAddress?.masked;
  const [ship, setShip] = useState(initialShip);
  const [bill, setBill] = useState(initialBill);
  const [note, setNote] = useState(order.note ?? '');
  const save = useApiMutation(
    () => {
      const shipChanged = addrComplete(ship) && JSON.stringify(ship) !== JSON.stringify(initialShip);
      const billChanged =
        order.edit.billing && addrComplete(bill) && JSON.stringify(bill) !== JSON.stringify(initialBill);
      return api.patch(
        `/v1/admin/orders/${order.id}`,
        {
          ...(shipChanged ? { shippingAddress: addrPayload(ship, order.shippingAddress) } : {}),
          ...(billChanged ? { billingAddress: addrPayload(bill, order.billingAddress) } : {}),
          ...((note.trim() || null) !== (order.note ?? null) ? { note: note.trim() || null } : {}),
        },
        { ifMatch: order.version },
      );
    },
    {
      invalidate: [['order', order.id], ['orders']],
      success: t('common.saved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      variant="sheet"
      title={t('admin.orderTools.editAddressTitle')}
      description={t('admin.orderTools.editAddressLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <ErrorList err={save.error} />
        {masked && <p className="text-sm text-ink-3">{t('admin.orderTools.addressMasked')}</p>}
        <div>
          <p className="mb-2 text-sm font-medium">{t('admin.orders.shippingAddress')}</p>
          <AddressFields
            value={ship}
            onChange={setShip}
            err={save.error}
            prefix="shippingAddress"
            required={false}
          />
        </div>
        {order.edit.billing ? (
          <div>
            <p className="mb-2 text-sm font-medium">{t('admin.orders.billingAddress')}</p>
            <AddressFields
              value={bill}
              onChange={setBill}
              err={save.error}
              prefix="billingAddress"
              required={false}
            />
          </div>
        ) : (
          order.edit.billingLock && (
            <p className="text-sm text-ink-3">{t(`errors.${order.edit.billingLock}`)}</p>
          )
        )}
        <Field label={t('admin.orders.customerNote')} optionalLabel={t('common.optional')}>
          {(p) => (
            <Textarea {...p} className="min-h-16" value={note} onChange={(e) => setNote(e.target.value)} />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

/** Sipariş ayrıntısında düzenleme düğmeleri ve kilit nedeni. */
export function OrderEditActions({ order }: { order: OrderDetail }) {
  const t = useT();
  const [open, setOpen] = useState<'draft' | 'lines' | 'address' | null>(null);
  const e = order.edit;
  if (!e.canWrite) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {e.all ? (
        <span className="inline-flex items-center gap-1">
          <Button size="sm" onClick={() => setOpen('draft')}>
            {t('admin.orderTools.editDraft')}
          </Button>
          <HelpTip k="orders.edit_draft" />
        </span>
      ) : (
        <>
          {e.lines && (
            <span className="inline-flex items-center gap-1">
              <Button size="sm" onClick={() => setOpen('lines')}>
                {t('admin.orderTools.editLines')}
              </Button>
              <HelpTip k="orders.edit_lines" />
            </span>
          )}
          {e.address && (
            <span className="inline-flex items-center gap-1">
              <Button size="sm" onClick={() => setOpen('address')}>
                {t('admin.orderTools.editAddress')}
              </Button>
              <HelpTip k="orders.edit_address" />
            </span>
          )}
          {!e.lines && e.linesLock && e.linesLock !== 'order_closed' && (
            <p className="text-sm text-ink-3">{t(`errors.${e.linesLock}`)}</p>
          )}
        </>
      )}
      {open === 'draft' && <OrderFormDialog order={order} onClose={() => setOpen(null)} />}
      {open === 'lines' && <OrderLinesDialog order={order} onClose={() => setOpen(null)} />}
      {open === 'address' && <OrderAddressDialog order={order} onClose={() => setOpen(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sipariş listesi: toplu işlem çubuğu
// ---------------------------------------------------------------------------

const BULK_TARGETS = ['processing', 'delivered', 'completed', 'cancelled'] as const;

export function OrderBulkBar({ ids, onClear }: { ids: string[]; onClear: () => void }) {
  const t = useT();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [statusOpen, setStatusOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const exportSelected = async () => {
    setBusy(true);
    try {
      const { blob, fileName } = await api.blob(
        `/v1/admin/exports/orders?format=xlsx&ids=${encodeURIComponent(ids.join(','))}`,
      );
      const name = fileName ?? 'siparisler.xlsx';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('admin.exports.downloaded', { name }));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.title : String(e));
    } finally {
      setBusy(false);
    }
  };
  const print = (tur: 'siparis' | 'sevk') =>
    void navigate({ to: '/siparis-yazdir', search: { ids: ids.join(','), tur } });
  return (
    <div
      role="region"
      aria-label={t('admin.orderTools.bulkRegion')}
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-line bg-surface-2 px-4 py-2 text-sm"
    >
      <span className="font-medium" aria-live="polite">
        {t('admin.orderTools.selected', { count: ids.length })}
      </span>
      <span className="ms-auto flex flex-wrap items-center gap-1">
        <Button size="sm" variant="ghost" onClick={onClear}>
          {t('admin.productBulk.clear')}
        </Button>
        <Button size="sm" onClick={() => print('siparis')}>
          {t('admin.orderTools.printOrders')}
        </Button>
        <Button size="sm" onClick={() => print('sevk')}>
          {t('admin.orderTools.printPacking')}
        </Button>
        <HelpTip k="orders.bulk_print" />
        <Button size="sm" loading={busy} onClick={() => void exportSelected()}>
          {t('admin.orderTools.exportSelected')}
        </Button>
        <HelpTip k="orders.bulk_export" />
        {can('orders.order.write') && (
          <>
            <Button size="sm" variant="primary" onClick={() => setStatusOpen(true)}>
              {t('admin.orderTools.bulkStatus')}
            </Button>
            <HelpTip k="orders.bulk_status" />
          </>
        )}
      </span>
      {statusOpen && <BulkStatusDialog ids={ids} onClose={() => setStatusOpen(false)} onDone={onClear} />}
    </div>
  );
}

function BulkStatusDialog({
  ids,
  onClose,
  onDone,
}: {
  ids: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const [to, setTo] = useState<(typeof BULK_TARGETS)[number]>('processing');
  const [note, setNote] = useState('');
  const [idem] = useState(() => new IdempotencyKey());
  const [result, setResult] = useState<BulkOrderStatusResult | null>(null);
  const run = useApiMutation(
    () =>
      api.post<BulkOrderStatusResult>(
        '/v1/admin/orders/bulk/status',
        { ids, to, note: note.trim() || undefined },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['orders']],
      onSuccess: (r) => {
        setResult(r);
        if (r.succeeded) toast.success(t('admin.orderTools.bulkDone', { count: r.succeeded }));
      },
    },
  );
  const failed = result?.results.filter((r) => !r.ok) ?? [];
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (v) return;
        onClose();
        if (result) onDone();
      }}
      title={t('admin.orderTools.bulkStatusTitle', { count: ids.length })}
      description={t('admin.orderTools.bulkStatusLead')}
      closeLabel={t('common.close')}
      footer={
        result ? (
          <Button
            variant="primary"
            onClick={() => {
              onClose();
              onDone();
            }}
          >
            {t('common.close')}
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button
              variant={to === 'cancelled' ? 'danger' : 'primary'}
              loading={run.isPending}
              onClick={() => run.mutate(undefined)}
            >
              {t('admin.orderTools.bulkApply')}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className="space-y-3 text-sm" role="status">
          <p>{t('admin.orderTools.bulkSummary', { ok: result.succeeded, failed: result.failed })}</p>
          {failed.length > 0 && (
            <ul className="divide-y divide-line rounded-md border border-line">
              {failed.map((r) => (
                <li key={r.id} className="px-3 py-2">
                  <span className="font-medium">{r.number ?? '—'}</span>:{' '}
                  {tOr(
                    t,
                    `errors.${r.code}`,
                    r.code ?? '',
                    (r.params ?? {}) as Record<string, string | number>,
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <Field label={t('admin.orderTools.bulkTarget')}>
            {(p) => (
              <Select {...p} value={to} onChange={(e) => setTo(e.target.value as typeof to)}>
                {BULK_TARGETS.map((s) => (
                  <option key={s} value={s}>
                    {t(`admin.orders.actions.${s}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <p className="text-sm text-ink-2">{t(`admin.orders.actionHints.${to}`)}</p>
          {to === 'cancelled' && (
            <p className="text-sm text-rose">{t('admin.orderTools.bulkCancelWarning')}</p>
          )}
          <Field label={t('admin.orders.statusNote')} optionalLabel={t('common.optional')}>
            {(p) => (
              <Textarea {...p} className="min-h-16" value={note} onChange={(e) => setNote(e.target.value)} />
            )}
          </Field>
        </div>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Yazdırma sayfası: sipariş özeti ve sevk listesi (tarayıcıdan yazdır veya PDF olarak kaydet)
// ---------------------------------------------------------------------------

export const printSearch = (s: Record<string, unknown>) => ({
  ids: typeof s.ids === 'string' ? s.ids : '',
  tur: (s.tur === 'sevk' ? 'sevk' : 'siparis') as 'siparis' | 'sevk',
});

export function addressLines(a: Record<string, unknown> | null): string[] {
  if (!a) return [];
  const s = (k: string) => (typeof a[k] === 'string' && (a[k] as string).trim() ? (a[k] as string) : null);
  return [
    s('companyName'),
    s('fullName'),
    s('line1'),
    s('line2'),
    [s('postalCode'), s('district'), s('city')].filter(Boolean).join(' ') || null,
    s('phone'),
    s('taxOffice') || s('taxNumber') ? [s('taxOffice'), s('taxNumber')].filter(Boolean).join(' / ') : null,
  ].filter((x): x is string => !!x);
}

export function OrderPrintPage() {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { ids, tur } = useSearch({ from: '/app/siparis-yazdir' });
  const kind = tur === 'sevk' ? 'packing' : 'order';
  const q = useQuery({
    queryKey: ['order-print', ids, kind],
    queryFn: () => api.get<OrderPrint>(`/v1/admin/orders/print?ids=${encodeURIComponent(ids)}&kind=${kind}`),
    enabled: !!ids,
  });
  const title = kind === 'packing' ? t('admin.orderPrint.packingTitle') : t('admin.orderPrint.orderTitle');
  return (
    <PageFrame
      helpKey="page.order_print"
      title={title}
      printable
      actions={
        <span className="inline-flex items-center gap-1">
          <Link to="/siparisler" className={buttonStyles({ variant: 'secondary' })}>
            {t('admin.orders.backToList')}
          </Link>
          <Button variant="primary" onClick={() => window.print()} disabled={!q.data}>
            <Printer className="h-4 w-4" aria-hidden />
            {t('admin.orderPrint.print')}
          </Button>
          <HelpTip k="orders.print" />
        </span>
      }
    >
      {q.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : !q.data ? (
        <p className="text-sm text-ink-3">{t('admin.orderPrint.nothing')}</p>
      ) : (
        <>
          <p className="text-sm text-ink-2 print:hidden">
            {t('admin.orderPrint.lead', { count: q.data.orders.length })}
            {q.data.personalData === 'masked' && ` ${t('admin.orderPrint.maskedNote')}`}
          </p>
          {q.data.orders.map((o) => (
            <article
              key={o.id}
              aria-label={t('admin.orderPrint.documentLabel', { title, number: o.number })}
              className="break-after-page space-y-5 rounded-md border border-line bg-surface p-6 text-sm text-ink print:break-inside-avoid print:rounded-none print:border-0 print:p-0"
            >
              <header className="flex flex-wrap items-start justify-between gap-4 border-b border-line pb-4">
                <div>
                  <p className="text-ink-2">{o.store}</p>
                  <h2 className="font-cond text-2xl font-semibold">{title}</h2>
                  {kind === 'packing' && (
                    <p className="text-xs text-ink-3">{t('admin.orderPrint.packingNote')}</p>
                  )}
                </div>
                <div className="text-end">
                  <p className="font-cond text-xl font-semibold tabular">{o.number}</p>
                  <p className="text-ink-2">{formatDate(o.placedAt ?? o.createdAt, true)}</p>
                  <Badge tone="neutral" className="mt-1">
                    {t(`admin.orders.statuses.${o.status}`)}
                  </Badge>
                </div>
              </header>
              <div className="grid gap-4 sm:grid-cols-2 print:grid-cols-2">
                <section>
                  <h3 className="mb-1 font-medium">{t('admin.orders.shippingAddress')}</h3>
                  {o.shippingAddress?.masked ? (
                    <p className="text-ink-2">
                      {[o.shippingAddress.district, o.shippingAddress.city].filter(Boolean).join(' / ')} (
                      {t('admin.customers.masked')})
                    </p>
                  ) : (
                    addressLines(o.shippingAddress).map((l) => <p key={l}>{l}</p>)
                  )}
                </section>
                <section>
                  {kind === 'order' && (
                    <>
                      <h3 className="mb-1 font-medium">{t('admin.orders.billingAddress')}</h3>
                      {o.billingAddress?.masked ? (
                        <p className="text-ink-2">({t('admin.customers.masked')})</p>
                      ) : (
                        addressLines(o.billingAddress).map((l) => <p key={l}>{l}</p>)
                      )}
                    </>
                  )}
                  <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                    <dt className="text-ink-2">{t('admin.orders.customer')}</dt>
                    <dd>{o.customerName ?? o.companyName ?? t('admin.orderTools.customerKinds.guest')}</dd>
                    {o.email && (
                      <>
                        <dt className="text-ink-2">{t('admin.customers.email')}</dt>
                        <dd>{o.email}</dd>
                      </>
                    )}
                    <dt className="text-ink-2">{t('admin.orders.shippingMethod')}</dt>
                    <dd>{o.shippingMethodName ?? '—'}</dd>
                    <dt className="text-ink-2">{t('admin.orders.paymentMethod')}</dt>
                    <dd>{o.paymentMethod ? t(`admin.orders.paymentMethods.${o.paymentMethod}`) : '—'}</dd>
                  </dl>
                </section>
              </div>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-line text-ink-2">
                    <th className="py-1.5 pe-2 text-start font-medium">{t('admin.products.sku')}</th>
                    <th className="py-1.5 pe-2 text-start font-medium">{t('admin.products.product')}</th>
                    <th className="py-1.5 pe-2 text-end font-medium">{t('admin.orders.qty')}</th>
                    {kind === 'order' ? (
                      <>
                        <th className="py-1.5 pe-2 text-end font-medium">{t('admin.orders.unitPrice')}</th>
                        <th className="py-1.5 pe-2 text-end font-medium">{t('admin.orders.tax')}</th>
                        <th className="py-1.5 text-end font-medium">{t('admin.orders.lineTotal')}</th>
                      </>
                    ) : (
                      <>
                        <th className="py-1.5 pe-2 text-start font-medium">{t('admin.orderPrint.lots')}</th>
                        <th className="py-1.5 text-center font-medium">{t('admin.orderPrint.check')}</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {o.lines.map((l, i) => (
                    <tr key={`${l.sku}-${i}`} className="border-b border-line align-top">
                      <td className="py-1.5 pe-2 tabular">{l.sku}</td>
                      <td className="py-1.5 pe-2">{l.title}</td>
                      <td className="py-1.5 pe-2 text-end tabular">{l.quantity}</td>
                      {kind === 'order' ? (
                        <>
                          <td className="py-1.5 pe-2 text-end tabular">
                            {money(l.unitPriceMinor, o.currency)}
                          </td>
                          <td className="py-1.5 pe-2 text-end tabular">
                            {l.taxRateBps === null ? '—' : bpsLabel(l.taxRateBps)}
                          </td>
                          <td className="py-1.5 text-end tabular">{money(l.lineTotalMinor, o.currency)}</td>
                        </>
                      ) : (
                        <>
                          <td className="py-1.5 pe-2">
                            {l.lots.length
                              ? l.lots
                                  .map((x) =>
                                    t('admin.orderPrint.lotLine', {
                                      lot: x.lotNo ?? '?',
                                      expiry: x.expiryDate
                                        ? formatDate(`${x.expiryDate}T12:00:00`, false)
                                        : '—',
                                      qty: x.quantity,
                                    }),
                                  )
                                  .join('; ')
                              : '—'}
                          </td>
                          <td className="py-1.5 text-center">
                            <span className="inline-block h-4 w-4 border border-ink-3" aria-hidden />
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {o.totals && (
                <dl className="ms-auto grid max-w-xs grid-cols-[1fr_auto] gap-x-4 gap-y-0.5">
                  <dt className="text-ink-2">{t('admin.orders.subtotal')}</dt>
                  <dd className="text-end tabular">{money(o.totals.subtotalMinor, o.currency)}</dd>
                  {o.totals.discountMinor > 0 && (
                    <>
                      <dt className="text-ink-2">
                        {t('admin.orders.discount', { code: o.totals.discountCode ?? '' })}
                      </dt>
                      <dd className="text-end tabular">−{money(o.totals.discountMinor, o.currency)}</dd>
                    </>
                  )}
                  <dt className="text-ink-2">{t('admin.orders.shipping')}</dt>
                  <dd className="text-end tabular">{money(o.totals.shippingMinor, o.currency)}</dd>
                  {o.totals.codFeeMinor > 0 && (
                    <>
                      <dt className="text-ink-2">{t('admin.orders.codFee')}</dt>
                      <dd className="text-end tabular">{money(o.totals.codFeeMinor, o.currency)}</dd>
                    </>
                  )}
                  <dt className="text-ink-2">{t('admin.orders.tax')}</dt>
                  <dd className="text-end tabular">{money(o.totals.taxMinor, o.currency)}</dd>
                  <dt className="font-medium">{t('admin.orders.total')}</dt>
                  <dd className="text-end font-semibold tabular">{money(o.totals.totalMinor, o.currency)}</dd>
                </dl>
              )}
              {o.note && (
                <p>
                  <span className="font-medium">{t('admin.orders.customerNote')}:</span> {o.note}
                </p>
              )}
              {kind === 'packing' && (
                <div className="grid grid-cols-2 gap-8 pt-6">
                  <p className="border-t border-ink-3 pt-1 text-ink-2">{t('admin.orderPrint.preparedBy')}</p>
                  <p className="border-t border-ink-3 pt-1 text-ink-2">{t('admin.orderPrint.checkedBy')}</p>
                </div>
              )}
            </article>
          ))}
        </>
      )}
    </PageFrame>
  );
}
